import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import FormControl from "@mui/material/FormControl";
import InputLabel from "@mui/material/InputLabel";
import MenuItem from "@mui/material/MenuItem";
import Select from "@mui/material/Select";
import TextField from "@mui/material/TextField";
import type {ColumnDef} from "@tanstack/react-table";
import {ContentStatus} from "crqlar-react-common/dist/models/content/contentCategory.model";
import type {Content} from "crqlar-react-common/dist/models/content/content.model";
import {ContentType} from "crqlar-react-common/dist/models/content/content.model";
import {ContentService} from "crqlar-react-common/dist/services/content/content.service";
import {useTrackScreenVisit} from "crqlar-react-common/dist/utils/analytics.util";
import {formatFullDateTime} from "crqlar-react-common/dist/utils/date.util";
import {useAllContentCategories} from "crqlar-react-common/dist/utils/hooks/content/useContentCategories";
import {ellpisize} from "crqlar-react-common/dist/utils/text.util";
import {useMemo} from "react";
import {useTranslation} from "react-i18next";
import {useNavigate, useSearchParams} from "react-router-dom";
import {
    ServerTable,
    ServerTableColumns,
    ServerTableCreate,
    ServerTableFrame,
    ServerTableFullscreen,
    ServerTableProvider,
    ServerTableSearch,
    ServerTableSort,
    ServerTableToolbar,
    useServerTable
} from "../../common/serverTable";
import SettingsListPage from "../layout/SettingsListPage";

const ContentList = () => {
    useTrackScreenVisit('system content');

    const navigate = useNavigate();
    const {t} = useTranslation();
    const [searchParams, setSearchParams] = useSearchParams();

    const filterStatus = searchParams.get('ContentListStatus') || '';
    const filterType = searchParams.get('ContentListType') || '';
    const filterCategoryIds = searchParams.get('ContentListCategoryIds')?.split(',').filter(Boolean) || [];

    const allCategories = useAllContentCategories();
    const categoriesById = useMemo(
        () => new Map(allCategories
            .filter(category => !!category.id)
            .map(category => [category.id!, category.name || ''])),
        [allCategories]
    );
    const selectedCategories = allCategories.filter(category => filterCategoryIds.includes(category.id!));

    const updateFilterParam = (key: string, value?: string) => {
        const newParams = new URLSearchParams(searchParams);
        if (value) {
            newParams.set(key, value);
        } else {
            newParams.delete(key);
        }
        newParams.delete('ContentListListPage');
        setSearchParams(newParams);
    };

    const columns = useMemo<ColumnDef<Content>[]>(() => [
        {
            id: 'name',
            header: t('Name'),
            accessorFn: content => content.name,
            cell: ({row}) => <Box>{row.original.name || '-'}</Box>,
            size: 280,
            meta: {defaultActive: true, isServerSortable: true, grow: true}
        },
        {
            id: 'categories',
            header: t('Categories'),
            accessorFn: content => content.categoryIds,
            cell: ({row}) => (
                <Box>{ellpisize(row.original.categoryIds
                    ?.map(id => categoriesById.get(id))
                    .filter(Boolean)
                    .join(', '), 50)}</Box>
            ),
            size: 240,
            meta: {defaultActive: true}
        },
        {
            id: 'type',
            header: t('Type'),
            accessorFn: content => content.type,
            cell: ({row}) => (
                <Box>
                    {row.original.type === ContentType.POST
                        ? t('Post')
                        : row.original.type === ContentType.APP_DATA
                            ? t('App Data')
                            : '-'}
                </Box>
            ),
            size: 160,
            meta: {defaultActive: true}
        },
        {
            id: 'status',
            header: t('Status'),
            accessorFn: content => content.status,
            cell: ({row}) => (
                <Box>
                    {row.original.status === ContentStatus.DRAFT
                        ? t('Draft')
                        : row.original.status === ContentStatus.ACTIVE
                            ? t('Active')
                            : '-'}
                </Box>
            ),
            size: 160,
            meta: {defaultActive: true}
        },
        {
            id: 'id',
            header: t('ID'),
            accessorFn: content => content.id,
            cell: ({row}) => <Box>{row.original.id}</Box>,
            size: 300,
            meta: {defaultActive: false, isServerSortable: true}
        },
        {
            id: 'updateDate',
            header: t('Last updated'),
            accessorFn: content => content.updateDate,
            cell: ({row}) => <Box>{formatFullDateTime(row.original.updateDate)}</Box>,
            size: 200,
            meta: {defaultActive: true, isServerSortable: true}
        }
    ], [categoriesById, t]);

    const controller = useServerTable({
        tableId: 'system-content',
        columns,
        getRowId: content => content.id!,
        onRowClick: content => navigate(`/system/contents/${content.id}`),
        initialSorting: [{id: 'id', desc: true}],
        queryDependencies: [filterStatus, filterType, filterCategoryIds.join(',')],
        queryFn: ({pagination, searchQuery, sorting}) => ContentService.getAllContents({
            limit: {
                offset: pagination.pageIndex * pagination.pageSize,
                limit: pagination.pageSize,
                sort: sorting ? [sorting] : undefined
            },
            query: searchQuery || undefined,
            status: filterStatus ? filterStatus as ContentStatus : undefined,
            type: filterType ? filterType as ContentType : undefined,
            categoryIds: filterCategoryIds.length ? filterCategoryIds : undefined
        }).then(page => ({
            content: page.content ?? [],
            totalElements: page.totalElements ?? 0
        }))
    });

    return (
        <SettingsListPage title={t('Contents')} back={() => navigate('/system')}>
            <ServerTableProvider controller={controller}>
                <ServerTableFrame>
                    <ServerTableToolbar hoistOnCompact>
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5,
                                flexWrap: 'wrap',
                                minWidth: 0,
                                mr: 'auto',
                                width: {xs: '100%', sm: 'auto'}
                            }}
                        >
                            <FormControl size="small" sx={{width: 108}}>
                                <InputLabel>{t('Status')}</InputLabel>
                                <Select
                                    value={filterStatus}
                                    label={t('Status')}
                                    onChange={event => updateFilterParam('ContentListStatus', event.target.value)}
                                >
                                    <MenuItem value="">{t('All')}</MenuItem>
                                    <MenuItem value={ContentStatus.DRAFT}>{t('Draft')}</MenuItem>
                                    <MenuItem value={ContentStatus.ACTIVE}>{t('Active')}</MenuItem>
                                </Select>
                            </FormControl>

                            <FormControl size="small" sx={{width: 108}}>
                                <InputLabel>{t('Type')}</InputLabel>
                                <Select
                                    value={filterType}
                                    label={t('Type')}
                                    onChange={event => updateFilterParam('ContentListType', event.target.value)}
                                >
                                    <MenuItem value="">{t('All')}</MenuItem>
                                    <MenuItem value={ContentType.POST}>{t('Post')}</MenuItem>
                                    <MenuItem value={ContentType.APP_DATA}>{t('App Data')}</MenuItem>
                                </Select>
                            </FormControl>

                            <Autocomplete
                                multiple
                                size="small"
                                limitTags={1}
                                options={allCategories}
                                getOptionLabel={category => category.name || ''}
                                value={selectedCategories}
                                onChange={(_, categories) => updateFilterParam(
                                    'ContentListCategoryIds',
                                    categories.map(category => category.id!).join(',')
                                )}
                                renderInput={params => <TextField {...params} label={t('Categories')}/>}
                                isOptionEqualToValue={(option, value) => option.id === value.id}
                                sx={{width: {xs: '100%', sm: 200}}}
                            />
                        </Box>

                        <ServerTableSearch/>
                        <ServerTableSort/>
                        <ServerTableFullscreen/>
                        <ServerTableColumns/>
                        <ServerTableCreate
                            label={t('Create content')}
                            onClick={() => navigate('/system/contents/new')}
                        />
                    </ServerTableToolbar>

                    <ServerTable/>
                </ServerTableFrame>
            </ServerTableProvider>
        </SettingsListPage>
    );
};

export default ContentList;
